import { useQuery } from '@powersync/react';
import { useCallback, useEffect } from 'react';

import { setTheme as setThemeCommand } from '@flow/domain';
import { applyLocalOperations, queries } from '@flow/sync';

import type { LocalDatabase } from '../db/powersync.js';
import { t } from '../i18n/es.js';

import { THEMES, type ThemePreference } from './theme.js';
import { useTheme } from './use-theme.jsx';

/**
 * Choosing a theme (APA-01, APA-03), shared by the appearance control and the command palette so
 * both apply it in this browser and store it in the user's synced preferences, unless the choice
 * is «solo en este dispositivo».
 */
export function useChooseTheme(local: LocalDatabase | null, userId: string | undefined) {
  const { setTheme } = useTheme();
  return useCallback(
    (next: ThemePreference, onlyHere = false) => {
      setTheme(next, { deviceOnly: onlyHere });
      // «Solo en este dispositivo» never touches the synced preference of the user.
      if (!onlyHere && local && userId) {
        void applyLocalOperations(local.db, setThemeCommand({ user_id: userId, theme: next }));
      }
    },
    [setTheme, local, userId],
  );
}

/**
 * Appearance control (APA-01, APA-03). Lives in the sidebar footer; the full Apariencia screen
 * (mockup 03) arrives with account settings.
 */
export function ThemeControl({ local, userId }: { local: LocalDatabase | null; userId?: string }) {
  const { preference, deviceOnly } = useTheme();
  const choose = useChooseTheme(local, userId);

  return (
    <fieldset className="theme-control">
      <legend>{t.theme.legend}</legend>
      {THEMES.map((value) => (
        <label key={value}>
          <input
            type="radio"
            name="theme"
            value={value}
            checked={preference === value}
            onChange={() => choose(value, deviceOnly)}
          />
          {t.theme.options[value]}
        </label>
      ))}
      <label>
        <input
          type="checkbox"
          checked={deviceOnly}
          onChange={(event) => choose(preference, event.target.checked)}
        />
        {t.theme.deviceOnly}
      </label>
    </fieldset>
  );
}

/**
 * Brings the user's synced theme into the app once the local database is open. Mounted inside the
 * PowerSync context; renders nothing.
 */
export function SyncedTheme({ userId }: { userId: string }) {
  const { receiveSyncedTheme } = useTheme();
  const query = queries.userPreferences(userId);
  const { data } = useQuery<{ theme: string | null }>(query.sql, query.params);
  const theme = data[0]?.theme ?? null;

  useEffect(() => {
    receiveSyncedTheme(theme);
  }, [theme, receiveSyncedTheme]);

  return null;
}
